import React from 'react'
import styled from 'styled-components'
import CrosshairCanvas from '@/components/Crosshair/Crosshair.jsx'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'
import ModuleContainer from './ModuleContainer'

import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'

const ContentWrapper = styled.div`
    width: 100%;
    max-width: 870px;
`

import { useUI } from '@/contexts/UIContext'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import { Tooltip } from '@mui/material'

const HeaderActionButton = styled.div`
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #fff;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 0.70rem;
    font-weight: 700;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        background: rgba(99, 102, 241, 0.2);
        border-color: #6366f1;
        transform: translateY(-1px);
    }
`

export default function CrosshairModule({ id, title, description, initialExpanded }) {
    const { t } = useLanguage()
    const { activeModules, configData, updateConfig, configIsLocked } = useConfig()
    const isActive = activeModules.includes('crosshair')
    const { showConfirm } = useUI()

    const displayTitle = title || t('CROSSHAIR_TITLE')
    const displayDesc = description || t('CROSSHAIR_DESC')

    const ch = configData.crosshair
    const presets = ch.presets || []

    const handleSavePreset = (e) => {
        e.stopPropagation()
        if (configIsLocked) return

        if (presets.length >= 10) {
            // Optional: Alert limit reached
            return
        }

        const currentCmds = [
            `cl_crosshairsize ${ch.cl_crosshairsize}`,
            `cl_crosshairthickness ${ch.cl_crosshairthickness}`,
            `cl_crosshairgap ${ch.cl_crosshairgap}`,
            `cl_crosshairalpha ${ch.cl_crosshairalpha}`,
            `cl_crosshairstyle ${ch.cl_crosshairstyle}`,
            `cl_crosshairdot ${ch.cl_crosshairdot}`,
            `cl_crosshair_drawoutline ${ch.cl_crosshair_drawoutline}`,
            `cl_crosshair_outlinethickness ${ch.cl_crosshair_outlinethickness}`,
            `cl_crosshaircolor_r ${ch.cl_crosshaircolor_r}`,
            `cl_crosshaircolor_g ${ch.cl_crosshaircolor_g}`,
            `cl_crosshaircolor_b ${ch.cl_crosshaircolor_b}`,
            `cl_crosshair_recoil ${ch.cl_crosshair_recoil}`,
            `cl_crosshair_t ${ch.cl_crosshair_t}`,
            `cl_crosshair_friendly_warning ${ch.cl_crosshair_friendly_warning}`,
            `cl_crosshair_sniper_width ${ch.cl_crosshair_sniper_width || 1}`
        ].join('; ')

        const newPreset = {
            name: `Preset ${presets.length + 1}`,
            commands: currentCmds,
            id: Date.now()
        }

        updateConfig('crosshair', 'presets', [...presets, newPreset])
    }

    return (
        <ModuleContainer
            id={id}
            moduleKey="crosshair"
            title={displayTitle}
            description={displayDesc}
            icon={CenterFocusStrongIcon}
            initialExpanded={initialExpanded}
            headerActions={
                <Tooltip
                    title={
                        <div style={{ textAlign: 'center' }}>
                            <strong>{t('MULTI_CROSSHAIR_SYSTEM')}</strong>
                            <br />
                            {t('SAVE_PRESET_DESC')}
                        </div>
                    }
                    arrow
                    placement="top"
                >
                    <HeaderActionButton onClick={handleSavePreset} disabled={configIsLocked || presets.length >= 10}>
                        <PlaylistAddIcon style={{ fontSize: 16 }} />
                        {t('SAVE_TO_SLOTS')}
                    </HeaderActionButton>
                </Tooltip>
            }

        >
            <ContentWrapper isActive={isActive}>
                <CrosshairCanvas />
            </ContentWrapper>
        </ModuleContainer>
    )
}
